import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Router } from '@angular/router';
import * as cashierActions from './cashierActions';
import { exhaustMap, map, catchError, tap } from 'rxjs/operators';
import { CashierService } from '../../services/cashier/cashier.service';
import { HttpErrorResponse } from '@angular/common/http';
import { of } from 'rxjs';
import { Balance } from '../../modules/cashier/balance';
import { PaymentSystems } from '../../modules/cashier/paymentSystems';
import { DepositResponse } from '../../modules/cashier/depositResponse';
import { BankAccountInfo } from '../../modules/cashier/bankAccountInfo';
import { WithdrawSystems } from '../../modules/cashier/withDraw';
import { TrasactionHistory } from '../../modules/cashier/trasactionhistory';
import { addBankAccountResponse } from '../../modules/cashier/addbankaccount';
import { LoginService } from '../../services/login/login.service';

const handleError = (error: any, actions: any) => {
    if (error.error instanceof Error) {
        return of(new actions({ message: error.error.message }));
    } else {
        try {
            if (error.error.message) {
                return of(new actions({ message: error.error.message }));
            } else if (error.message) {
                return of(new actions({ message: error.message }));
            }
            return of(new actions({ message: "Something went wrong, please contact admin." }));
        } catch {
            // Handle any parsing errors silently
        }
    }
    return of(new actions({ message: "Something went wrong, please contact admin." }));
};

@Injectable()
export class CashierEffects {
    constructor(
        private actions$: Actions,
        private router: Router,
        private cashierservice: CashierService,
        private loginservice: LoginService
    ) {}

    cashierGetBalance$ = createEffect(() => this.actions$.pipe(
        ofType(cashierActions.CASHIER_GET_BALANCE),
        exhaustMap((action: cashierActions.CashierGetBalanceStart) =>
            this.cashierservice.onCashierGetBalance().pipe(
                map((response: Balance) => new cashierActions.CashierGetBalanceSuccess(response)),
                catchError((error: HttpErrorResponse) => handleError(error, cashierActions.CashierGetBalanceFail))
            )
        )
    ));

    cashierGetBalanceSuccess$ = createEffect(() => this.actions$.pipe(
        ofType(cashierActions.CASHIER_GET_BALANCE_SUCCESS),
        tap((action: cashierActions.CashierGetBalanceSuccess) => {
            const { payload } = action;
            if (payload && payload.code) { 
                if (payload.code === 'SESSION_EXPIRED') {
                    this.cashierservice.sessionExpire(true);
                    this.loginservice.onPlayerLoggedIn(false); 
                } else {
                    this.cashierservice.sessionExpire(false);
                }
            }
        })
    ), { dispatch: false });

    cashierResetPlaymoney$ = createEffect(() => this.actions$.pipe(
        ofType(cashierActions.CASHIER_RESET_PLAYMONEY),
        exhaustMap((action: cashierActions.CashierResetPlayMoneyStart) =>
            this.cashierservice.onCashierResetPlayMoney().pipe(
                map(response => new cashierActions.CashierResetPlayMoneySuccess(response)),
                catchError((error: HttpErrorResponse) => handleError(error, cashierActions.CashierResetPlayMoneyFail))
            )
        )
    ));

    // cashierResetPlaymoneySuccess$ = createEffect(() => this.actions$.pipe(
    //     ofType(cashierActions.CASHIER_RESET_PLAYMONEY_SUCCESS),
    //     tap((action: cashierActions.CashierResetPlayMoneySuccess) => {
    //         if (action.payload.success === true) {
    //             this.router.navigate(['/profile']);
    //         }
    //     })
    // ), { dispatch: false });

    cashierGetPaymentSystem$ = createEffect(() => this.actions$.pipe(
        ofType(cashierActions.CASHIER_GET_PAYMENTS_SYSTEMS),
        exhaustMap((action: cashierActions.CashierGetPaymentSystemsStart) =>
            this.cashierservice.onCashierGetPaymentSystems().pipe(
                map((response: PaymentSystems) => new cashierActions.CashierGetPaymentSystemsSuccess(response)),
                catchError((error: HttpErrorResponse) => handleError(error, cashierActions.CashierGetPaymentSystemsFail))
            )
        )
    ));

    cashierDeposit$ = createEffect(() => this.actions$.pipe(
        ofType(cashierActions.CASHIER_DEPOSIT),
        exhaustMap((action: cashierActions.CashierDepositStart) =>
            this.cashierservice.onCashierDeposit(action.payload).pipe(
                map((response: DepositResponse) => new cashierActions.CashierDepositSuccess(response)),
                catchError((error: HttpErrorResponse) => handleError(error, cashierActions.CashierDepositFail))
            )
        )
    ));

    // cashierGetOpenWithdrawRequest$ = createEffect(() => this.actions$.pipe(
    //     ofType(cashierActions.CASHIER_OPEN_WITHDRAW_REQUEST),
    //     exhaustMap((action: cashierActions.CashierGetOpenWithDrawRequest) =>
    //         this.cashierservice.onCashierGetOpenWithdrawRequest().pipe(
    //             map(response => new cashierActions.CashierGetOpenWithDrawRequestSuccess(response)),
    //             catchError((error: HttpErrorResponse) => handleError(error, cashierActions.CashierGetOpenWithDrawRequestFail))
    //         )
    //     )
    // ));

    cashierWithdrawRequest$ = createEffect(() => this.actions$.pipe(
        ofType(cashierActions.CASHIER_WITHDRAW_REQUEST),
        exhaustMap((action: cashierActions.CashierWithDrawRequest) =>
            this.cashierservice.onCashierWithdrawRequest(action.payload).pipe(
                map(response => new cashierActions.CashierWithDrawRequestSuccess(response)),
                catchError((error: HttpErrorResponse) => handleError(error, cashierActions.CashierWithDrawRequestFail))
            )
        )
    ));

    cashierCancelWithdrawRequest$ = createEffect(() => this.actions$.pipe(
        ofType(cashierActions.CASHIER_CANCEL_WITHDRAW_REQUEST),
        exhaustMap((action: cashierActions.CashierCancelWithDrawRequest) =>
            this.cashierservice.onCashierCancelWithdrawRequest(action.payload).pipe(
                map(response => new cashierActions.CashierCancelWithDrawRequestSuccess(response)),
                catchError((error: HttpErrorResponse) => handleError(error, cashierActions.CashierCancelWithDrawRequestFail))
            )
        )
    ));

    // cashierCancelWithdrawRequestSuccess$ = createEffect(() => this.actions$.pipe(
    //     ofType(cashierActions.CASHIER_CANCEL_WITHDRAW_REQUEST_SUCCESS),
    //     tap((action: cashierActions.CashierCancelWithDrawRequestSuccess) => {
    //         if (action.payload.success === true) {
    //             this.cashierservice.onCashierCancelWitdrawSuccess();
    //         }
    //     })
    // ), { dispatch: false });

    cashierTransactionHistory$ = createEffect(() => this.actions$.pipe(
        ofType(cashierActions.CASHIER_TRANSACTION_HISTORY),
        exhaustMap((action: cashierActions.CashierTransactionHistory) =>
            this.cashierservice.onCashierTransactionHistory(action.payload).pipe(
                map((response: TrasactionHistory) => new cashierActions.CashierTransactionHistorySuccess(response)),
                catchError((error: HttpErrorResponse) => handleError(error, cashierActions.CashierTransactionHistoryFail))
            )
        )
    ));

    // cashierAddBankAccount$ = createEffect(() => this.actions$.pipe(
    //     ofType(cashierActions.CASHIER_ADD_BANK_ACCOUNT),
    //     exhaustMap((action: cashierActions.CashierAddBankAccount) =>
    //         this.cashierservice.onCashierAddBankAccount(action.payload).pipe(
    //             map((response: addBankAccountResponse) => new cashierActions.CashierAddBankAccountSuccess(response)),
    //             catchError((error: HttpErrorResponse) => handleError(error, cashierActions.CashierAddBankAccountFail))
    //         )
    //     )
    // ));

    cashierAddBankAccountSuccess$ = createEffect(() => this.actions$.pipe(
        ofType(cashierActions.CASHIER_ADD_BANK_ACCOUNT_SUCCESS),
        tap((action: cashierActions.CashierAddBankAccountSuccess) => {
            if (action.payload.success === true) {
                this.cashierservice.getCashierBankAccounts();
            }
        })
    ), { dispatch: false });

    // cashierGetBankAccount$ = createEffect(() => this.actions$.pipe(
    //     ofType(cashierActions.CASHIER_GET_BANK_ACCOUNT),
    //     exhaustMap((action: cashierActions.CashierGetBankAccount) =>
    //         this.cashierservice.onCashierGetBankAccount().pipe(
    //             map((response: BankAccountInfo) => new cashierActions.CashierGetBankAccountSuccess(response)),
    //             catchError((error: HttpErrorResponse) => handleError(error, cashierActions.CashierGetBankAccountFail))
    //         )
    //     )
    // ));

    cashierDeleteBankAccount$ = createEffect(() => this.actions$.pipe(
        ofType(cashierActions.CASHIER_DELETE_BANK_ACCOUNT),
        exhaustMap((action: cashierActions.CashierDeleteBankAccount) =>
            this.cashierservice.onCashierDeleteBankAccount(action.payload).pipe(
                map((response: BankAccountInfo) => new cashierActions.CashierDeleteBankAccountSuccess(response)),
                catchError((error: HttpErrorResponse) => handleError(error, cashierActions.CashierDeleteBankAccountFail))
            )
        )
    ));

    // cashierDeleteBankAccountSuccess$ = createEffect(() => this.actions$.pipe(
    //     ofType(cashierActions.CASHIER_DELETE_BANK_ACCOUNT_SUCCESS),
    //     tap((action: cashierActions.CashierDeleteBankAccountSuccess) => {
    //         if (action.payload.success === true) {
    //             this.cashierservice.getCashierBankAccounts();
    //         }
    //     })
    // ), { dispatch: false });

    cashierGetDepositSystems$ = createEffect(() => this.actions$.pipe(
        ofType(cashierActions.CASHIER_GET_DEPOSIT_SYSTEMS),
        exhaustMap((action: cashierActions.CashierGetDepositSystems) =>
            this.cashierservice.onCashierGetDepositSystems(action.payload).pipe(
                map(response => new cashierActions.CashierGetDepositSystemsSuccess(response)),
                catchError((error: HttpErrorResponse) => handleError(error, cashierActions.CashierGetDepositSystemsFail))
            )
        )
    ));

    cashierGetWithdrawSystems$ = createEffect(() => this.actions$.pipe(
        ofType(cashierActions.CASHIER_GET_WITHDRAW_SYSTEMS),
        exhaustMap((action: cashierActions.CashierGetWithdrawSystems) =>
            this.cashierservice.onCashierGetWithdrawSystems(action.payload).pipe(
                map((response: WithdrawSystems) => new cashierActions.CashierGetWithdrawSystemsSuccess(response)),
                catchError((error: HttpErrorResponse) => handleError(error, cashierActions.CashierGetWithdrawSystemsFail))
            )
        )
    ));

    cashierWithdrawCashout$ = createEffect(() => this.actions$.pipe(
        ofType(cashierActions.CASHIER_WITHDRAW_CASHOUT),
        exhaustMap((action: cashierActions.CashierWithdrawCashout) =>
            this.cashierservice.onCashierWithdrawCashout(action.payload).pipe(
                map(response => new cashierActions.CashierWithdrawCashoutSuccess(response)),
                catchError((error: HttpErrorResponse) => handleError(error, cashierActions.CashierWithdrawCashoutFail))
            )
        )
    ));
}
